import React, { useState } from "react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { MessageCircle, Send, Mail, Phone, MapPin, Clock } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  const { site } = useSite();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill all required fields", description: "Name, email aur message zaruri hain." });
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast({ title: "Message Sent!", description: "Hum 24 hours me aapko reply karenge. WhatsApp par instant reply ke liye click karein." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast({ title: "Submission failed", description: e?.response?.data?.detail || "Please try again or WhatsApp directly." });
    } finally { setLoading(false); }
  };

  const channels = [
    { icon: MessageCircle, title: "WhatsApp", subtitle: "Fastest · 24/7 · 60 sec reply", action: "Chat Now", href: site.whatsappUrl, color: "text-green-400" },
    { icon: Send, title: "Telegram", subtitle: "Updates, results aur tips", action: "Join Channel", href: site.telegramUrl, color: "text-sky-400" },
    { icon: Mail, title: "Email", subtitle: site.email, action: "Send Email", href: `mailto:${site.email}`, color: "text-amber-400" },
    { icon: Phone, title: "Phone", subtitle: site.phone, action: "Call Now", href: `tel:${(site.phone || "").replace(/\s/g, "")}`, color: "text-rose-400" },
  ];

  return (
    <div>
      <SEO title={`Contact ${site.brand} - 24x7 WhatsApp & Telegram Support`} description="Get in touch with OnlineCricketID support team via WhatsApp, Telegram, email or phone. Average reply time under 60 seconds." />
      <PageHero title={`Contact ${site.brand}`} breadcrumb="Contact Us" subtitle="Koi sawaal, problem ya feedback? Hum 24x7 available hain. WhatsApp par average reply time 60 second se kam." />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {channels.map((c) => (
              <a key={c.title} href={c.href} target="_blank" rel="noreferrer" className="card-dark p-6 group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <c.icon className={`w-6 h-6 ${c.color}`} />
                </div>
                <h3 className="mt-4 text-lg font-bold text-white">{c.title}</h3>
                <p className="text-sm text-slate-400 mt-1 break-all">{c.subtitle}</p>
                <div className="mt-3 text-sm text-amber-400 font-semibold">{c.action} →</div>
              </a>
            ))}
          </div>

          <div className="mt-12 grid lg:grid-cols-[1.2fr_1fr] gap-8">
            <div className="card-dark p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white">Send us a message</h2>
              <p className="text-slate-400 text-sm mt-1">Hum jaldi se jaldi reply karenge. Urgent ke liye WhatsApp par message karein.</p>
              <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-1">
                  <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aapka naam" className="mt-1.5 bg-[#0c1530] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-500" />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="phone" className="text-slate-300">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 9xxxxxxxxx" className="mt-1.5 bg-[#0c1530] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email" className="text-slate-300">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1.5 bg-[#0c1530] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message" className="text-slate-300">Message *</Label>
                  <Textarea id="message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Apna sawaal yahan likhein..." className="mt-1.5 bg-[#0c1530] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-500" />
                </div>
                <div className="sm:col-span-2 flex flex-wrap gap-3">
                  <Button type="submit" disabled={loading} className="btn-gold border-0 px-6 py-5 rounded-xl text-sm">{loading ? "Sending..." : "Send Message"}</Button>
                  <a href={site.whatsappUrl} target="_blank" rel="noreferrer" className="btn-outline-gold inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp Instead</a>
                </div>
              </form>
            </div>

            <div className="space-y-5">
              <div className="card-dark p-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-amber-400 mt-1" />
                  <div>
                    <div className="text-white font-semibold">Head Office</div>
                    <div className="text-slate-400 text-sm mt-1">Connaught Place, New Delhi, India - 110001</div>
                  </div>
                </div>
              </div>
              <div className="card-dark p-6">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-400 mt-1" />
                  <div>
                    <div className="text-white font-semibold">Support Hours</div>
                    <div className="text-slate-400 text-sm mt-1">24x7 · WhatsApp aur Telegram par hamesha online</div>
                  </div>
                </div>
              </div>
              <div className="card-dark p-6">
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-amber-400 mt-1" />
                  <div>
                    <div className="text-white font-semibold">Direct Call</div>
                    <a href={`tel:${(site.phone || "").replace(/\s/g, "")}`} className="text-slate-300 text-sm mt-1 hover:text-amber-400 block">{site.phone}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
